/**
 * SellerCard — one seller row in the "Find shops" list (design revamp).
 *
 * Production translation of `SellerCard` in
 * `docs/GardEn/handoff-2026-06/garden/screens-shops.jsx`: an interactive
 * `Card` with an `Avatar`, the business name, a ★rating · reviews · district
 * line, then a warranty `Pill` (success tone, shield) + a languages `Pill` +
 * the positions count. Tapping the card opens the seller detail sheet.
 *
 * Tokens come from the `--g-*` Tailwind mirror; every string is a `shops`
 * i18n key (RU/KY/EN). Rating/positions are formatted with the `format`
 * helpers (rating parsed from the Decimal-as-string `rating` field) and use
 * `tabular-nums`. Color is never the sole signal — the warranty pill carries
 * a shield icon + text label, and the rating carries a star + numeric value.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { Avatar, Card, Icon, Pill } from "@garden/ui";
import { parseDecimal } from "@/lib/format";
import type { SellerSummary } from "@/lib/garden-api";
import type { SupportedLanguage } from "@/lib/i18n";
import { languageCode } from "@/lib/language-code";

export interface SellerCardProps {
  seller: SellerSummary;
  /** Opens the seller detail sheet. */
  onOpen: (seller: SellerSummary) => void;
  /** UI language — picks the market-name fallback (SCRUM-144). */
  lang: SupportedLanguage;
}

/** Up-to-two-character initials for the avatar (RU/KY business names). */
export function sellerInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`.toUpperCase();
}

export function SellerCard({
  seller,
  onOpen,
  lang,
}: SellerCardProps): ReactElement {
  const { t } = useTranslation("shops");
  const rating = parseDecimal(seller.rating);
  // SCRUM-144: `district` has no per-language variant (schema/API change,
  // out of scope here) — only the market-name fallback can be localized
  // from the fields already on the wire.
  const marketName =
    lang === "ky" ? (seller.market_name_ky ?? seller.market_name_ru) : seller.market_name_ru;
  const district = seller.district ?? marketName;

  return (
    <Card interactive padding={15} onClick={() => onOpen(seller)}>
      <div
        data-testid={`seller-card-${seller.id}`}
        className="flex gap-3"
      >
        <Avatar initials={sellerInitials(seller.business_name)} size={48} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="truncate text-[15px] font-bold text-foreground">
              {seller.business_name}
            </div>
            <Icon
              name="chevR"
              size={18}
              className="shrink-0 text-[var(--g-ink-3)]"
            />
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[var(--g-ink-2)]">
              <Icon
                name="star"
                size={13}
                className="fill-[var(--g-warn)] text-[var(--g-warn)]"
              />
              <span className="tabular-nums">{rating.toFixed(1)}</span>
            </span>
            <span className="text-xs text-[var(--g-ink-3)]">
              · {t("reviews", { count: seller.reviews_count })}
            </span>
            <span className="text-xs text-[var(--g-ink-3)]">· {district}</span>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            {seller.warranty ? (
              <Pill tone="success" size="sm" leading="shield">
                {t("warranty")}
              </Pill>
            ) : null}
            {seller.languages.length > 0 ? (
              <Pill tone="neutral" size="sm">
                {seller.languages.map(languageCode).join(" · ")}
              </Pill>
            ) : null}
            <span className="text-[11.5px] tabular-nums text-[var(--g-ink-3)]">
              {t("positionsCount", { count: seller.positions_count })}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}
